import { Forms } from '@app/modules/events/models/dashboard.model'
import { SlugAttribute } from '@personas/attributes/slug.attribute'
import { WeightAttribute } from '@personas/attributes/weight.attribute'
import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { PersonaTypes } from '@personas/enums/persona-types.enum'
import { PersonaFields } from '@personas/interfaces/persona-field.interface'
import { LandingPageOptions } from './persona-landing-option.interface'
import { PersonaOptions } from './persona-options.interface'
import { PromoCode } from './promo-code.interface'

/**
 * Defins a persona of an event
 */
export interface Persona extends WeightAttribute, SlugAttribute {
  /** Event id */
  event_id: number
  /** Persona id */
  id: number
  /** Does the persona require registrations approval? */
  require_approval: boolean
  /** Persona name */
  name: string
  /** Persona weight (order among other personas) */
  weight: number
  /**
   * Maximum allowed number of registrations
   */
  maximum_registrations: number
  /** Persona success message (on registration) */
  success_message: string
  /** Persona regret message (on registration denial) */
  regret_message: string
  /** Persona fields */
  form_elements: PersonaFields
  /** Persona type */
  type: PersonaTypes
  /** Persona option metadata */
  options: PersonaOptions
  /** Landing Poage options metadata */
  landing_page_options: LandingPageOptions
  /** Persona zones */
  checkins?: any[]
  /** Persona zones ids */
  sub_locations_ids?: number[]
  /** Persona currency id */
  currency_id?: number
  /** Persona currency @deprecated */
  currency?: any
  /** Persona registration fees */
  registration_amount?: number
  /** Whether the persona has payment enabled */
  enable_payment?: boolean
  /** Promo codes assigned to the persona */
  promo_codes?: PromoCode[]
  /** Promo code ids assigned to the persona */
  promo_code_ids?: PromoCode[]
  products_count: number
  form_email?: FormEmail
  email_subject: string
  email_from_name: string
  email_reply: string
  public_description?: string
  /** Passcode to protect persona */
  pass_code?: string
  parent?: number
  show_in_events?: boolean
  enable_communication?: boolean
  /** WhatsApp flags sent on the communication PATCH. */
  whatsapp_communication?: WhatsappCommunication
  /** SMS flags and message sent on the communication PATCH. */
  sms_communication?: SmsCommunication
  badge_front_html_content?: string
  forms?: Partial<Forms>[]
  slug: string
}

/** WhatsApp sends included on the persona communication PATCH. */
export interface WhatsappCommunication {
  enable_online: boolean
  enable_onsite: boolean
  enable_reminders: boolean
}

/** Email confirmation fields sent under mail_communication on the communication PATCH. */
export interface MailCommunication {
  email_reply?: string | null
  email_subject?: string | null
  email_from_name?: string | null
}

/** Body for PATCH .../personas/{id}/communication. Sent in addition to the forms create/update call. */
export interface PersonaCommunicationPayload {
  enable_communication?: boolean | null
  require_approval?: boolean | null
  success_message?: string | null
  regret_message?: string | null
  pending_approval_message?: string | null
  whatsapp_communication?: WhatsappCommunication | null
  sms_communication?: SmsCommunication | null
  mail_communication?: MailCommunication | null
}

export interface FormEmail {
  id?: number
  form_id?: number
  slug?: string
  uuid?: string
  email_trigger?: string
  email_from?: string
  email_from_name?: string
  email_reply?: string
  email_subject?: string
  email_content?: string
  email_to?: string
  email_to_name?: string
  is_invitation_attached?: number
  is_pdf_attached?: number
  is_sent_on_create?: number
  is_sent_on_update?: number
  is_sent_to_pr?: number
  /** WhatsApp flags returned on the email record. */
  whatsapp_communication?: WhatsappCommunication
  /** SMS flags and message returned on the email record. */
  sms_communication?: SmsCommunication
}

/** SMS sends included on the persona communication PATCH. Online and on-site only. */
export interface SmsCommunication {
  enable_online: boolean
  enable_onsite: boolean
  message: string | null
}

/** Defines a persona array */
export declare type Personas = Persona[]

/** Defines a persona currency */
export interface Currency {
  /** currency id */
  id: number
  /** currency name */
  name: string
  /** currency slug */
  slug: string
}
/**
 * Defines a persona badge template
 */
export interface BadgeTemplate {
  topPadding: number
  persona: {
    enabled: boolean
    fontSize: number
    lineHeight: number
  }
  qr: {
    enabled: boolean
    showText: boolean
    width: number
    fontSize: number
  }
  dtcm: {
    enabled: boolean
    showText: boolean
    height: number
    fontSize: number
  }
  fields?: {
    [key: string]: {
      ordinal: number
      enabled: boolean
      label: string
      name: string
      fontSize: number
      lineHeight: number
    }
  }
}

/**
 * Defines a default persona used in persona generation
 */
export const DEFAULT_PERSONA: Persona = {
  event_id: null,
  id: null,
  slug: 'TMP',
  require_approval: false,
  name: '',
  weight: 0,
  maximum_registrations: null,
  success_message:
    '<p>Thank you for registering.</p><p>Please keep this confirmation handy when you come to the venue (You don’t need to print it)</p><p>Please note that children under the age of 12 can not be permitted to this event.</p><p>If you have any questions please do not hesitate to contact us at: <span style="background-color: rgb(255, 255, 0);">xxx xxx xxx xxxx</span></p><p>See you soon at the event!</p><p>Events team</p>',
  regret_message:
    '<p>Dear {{firstname}},</p><p>We are sorry to inform you that your registration was not approved.</p><p>If you think this is a mistake - Kindly contact the organizer<p>',
  form_elements: [
    {
      form_element_type_id: PersonaFieldTypes.FULLNAME,
      label: 'Full name',
      weight: 0,
      name: 'fullname',
      is_required: 3,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.EMAIL,
      label: 'E-mail',
      weight: 1,
      name: 'email',
      is_required: 1,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PHONE,
      label: 'Phone number',
      weight: 2,
      name: 'phone',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.JOB_TITLE,
      label: 'Job title',
      weight: 3,
      name: 'job_title',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.ORGANIZATION,
      label: 'Organization',
      weight: 4,
      name: 'organization',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.COUNTRY,
      label: 'Country',
      weight: 5,
      name: 'country',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    }
  ],
  type: PersonaTypes.VISITOR,
  options: {
    handshakes: {
      coordinators: [],
      interactions: [],
      status: 0,
      version: 2
    },
    reminders: {
      enabled: true,
      schedules: [
        {
          type: 'hour',
          value: 8
        }
      ],
      status: []
    }
  },
  landing_page_options: generateLandingPageOptions(),
  promo_codes: [],
  products_count: 0,
  form_email: {},
  email_subject: '',
  email_from_name: '',
  email_reply: '',
  pass_code: null,
  show_in_events: false,
  enable_communication: true
}
/**
 * Defines a default visitor persona used as the first persona used in first time event generation
 */
export const VISITOR_PERSONA: Persona = {
  event_id: null,
  id: null,
  slug: 'VIS',
  require_approval: false,
  name: 'Visitor',
  weight: 0,
  maximum_registrations: null,
  success_message:
    '<p>Thank you for registering.</p><p>Please keep this confirmation handy when you come to the venue (You don’t need to print it)</p><br><p>Please note that children under the age of 12 can not be permitted to this event.</p><br><p>If you have any questions please do not hesitate to contact us at: <span style="background-color: rgb(255, 255, 0);">xxx xxx xxx xxxx</span></p><br><p>See you soon at the event!</p><p>Events team</p>',
  regret_message: 'Dear {{firstname}},<br> We are sorry to inform you that your registration was not approved. If you think this is a mistake - Kindly contact the organizer',
  form_elements: [
    {
      form_element_type_id: PersonaFieldTypes.FULLNAME,
      label: 'Full name',
      weight: 0,
      name: 'fullname',
      is_required: 3,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.EMAIL,
      label: 'E-mail',
      weight: 1,
      name: 'email',
      is_required: 1,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PHONE,
      label: 'Phone number',
      weight: 2,
      name: 'phone',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.JOB_TITLE,
      label: 'Job title',
      weight: 3,
      name: 'job_title',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.ORGANIZATION,
      label: 'Organization',
      weight: 4,
      name: 'organization',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.COUNTRY,
      label: 'Country',
      weight: 5,
      name: 'country',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    }
  ],
  type: PersonaTypes.VISITOR,
  options: {
    handshakes: {
      coordinators: [],
      interactions: [],
      status: 0,
      version: 2
    },
    reminders: {
      enabled: true,
      schedules: [
        {
          type: 'hour',
          value: 8
        }
      ],
      status: []
    }
  },
  landing_page_options: generateLandingPageOptions(),
  promo_codes: [],
  products_count: 0,
  form_email: {},
  email_subject: '',
  email_from_name: '',
  email_reply: '',
  pass_code: null,
  show_in_events: false,
  enable_communication: true
}

/** Defines a default generic persona used in first time event generation */
export const GENERIC_PERSONA: Persona = {
  event_id: null,
  id: null,
  slug: 'GEN',
  require_approval: false,
  name: 'Generic',
  weight: 1000,
  maximum_registrations: null,
  success_message: '',
  regret_message: 'Dear {{firstname}},<br> We are sorry to inform you that your registration was not approved. If you think this is a mistake - Kindly contact the organizer',
  form_elements: [
    {
      form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
      label: 'Category',
      weight: 0,
      name: 'category',
      is_required: 3,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
      label: 'Line 1',
      weight: 0,
      name: 'line1',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
      label: 'Line 2',
      weight: 0,
      name: 'line2',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
      label: 'Line 3',
      weight: 0,
      name: 'line3',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    },
    {
      form_element_type_id: PersonaFieldTypes.PLAIN_TEXT,
      label: 'Line 4',
      weight: 0,
      name: 'line4',
      is_required: 0,
      is_unique: false,
      is_disable_online: false,
      is_disable_onsite: false,
      form_element_values: []
    }
  ],
  type: PersonaTypes.GENERIC,
  options: {},
  landing_page_options: generateLandingPageOptions(),
  promo_codes: [],
  products_count: 0,
  form_email: {},
  email_subject: '',
  email_from_name: '',
  email_reply: '',
  pass_code: null
}

function generateLandingPageOptions(): LandingPageOptions {
  return {
    branding: { id: 1, text: 'Event branding', isEnabled: true },
    timing: { id: 2, text: 'Dates & Timings', isEnabled: false },
    about: { id: 3, text: 'About the event', isEnabled: false },
    downloads: { id: 4, text: 'Downloads', isEnabled: false, files: [] },
    location: { id: 5, text: 'Event location', isEnabled: false },
    form: { id: 6, text: 'Registration form', isEnabled: true, trackableMirrors: ['facebook'] },
    contacts: {
      id: 7,
      text: 'Organized by & Key contacts',
      isEnabled: false,
      contactsList: []
    }
  }
}
